
import React, { useEffect, useState } from 'react';
import { Check, Edit, Plus, Save, Trash2, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Tag {
  id: string;
  name: string;
  description: string | null;
  keywords: string[] | string | null;
  created_at: string;
}

export function AdminTagsTab() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [newTag, setNewTag] = useState<Partial<Tag> | null>(null);
  const [deleteTagId, setDeleteTagId] = useState<string | null>(null);
  const [keywordInput, setKeywordInput] = useState("");
  const [newTagKeywordInput, setNewTagKeywordInput] = useState("");
  const [showExtractTagsDialog, setShowExtractTagsDialog] = useState(false);
  const [extractTagsSlug, setExtractTagsSlug] = useState("");
  const [extractTagsVersion, setExtractTagsVersion] = useState("");
  const [extractTagsForce, setExtractTagsForce] = useState(true);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');

      if (error) {
        throw error;
      }

      // Normalize the data
      const normalizedTags = data.map(tag => ({
        ...tag,
        keywords: normalizeKeywords(tag.keywords)
      }));

      setTags(normalizedTags);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast({
        title: "Error fetching tags",
        description: "There was a problem loading the tags.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to normalize keywords to array format
  const normalizeKeywords = (keywords: Tag['keywords']): string[] => {
    if (!keywords) return [];
    if (Array.isArray(keywords)) return keywords;
    if (typeof keywords === 'string') {
      return keywords.split(',').map(k => k.trim()).filter(k => k !== '');
    }
    return [];
  };

  // Format keywords to string for display
  const formatKeywordsForDisplay = (keywords: Tag['keywords']): string => {
    const keywordsArray = normalizeKeywords(keywords);
    return keywordsArray.join(', ');
  };

  // Start editing a tag
  const handleEdit = (tag: Tag) => {
    setEditingTag({
      ...tag,
      keywords: normalizeKeywords(tag.keywords)
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingTag(null);
  };

  // Save edited tag
  const handleSave = async () => {
    if (!editingTag) return;

    try {
      // Format keywords for storage
      const keywordsForStorage: string[] = normalizeKeywords(editingTag.keywords);

      const { error } = await supabase
        .from('tags')
        .update({
          name: editingTag.name,
          description: editingTag.description,
          keywords: keywordsForStorage
        })
        .eq('id', editingTag.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Tag updated",
        description: "The tag was successfully updated.",
      });

      // Refresh tag list
      fetchTags();
      setEditingTag(null);
    } catch (error) {
      console.error('Error updating tag:', error);
      toast({
        title: "Error updating tag",
        description: "There was a problem updating the tag.",
        variant: "destructive",
      });
    }
  };

  // Add keyword to editing tag
  const handleAddKeyword = () => {
    if (!editingTag || !keywordInput.trim()) return;
    
    const currentKeywords = normalizeKeywords(editingTag.keywords);
    if (!currentKeywords.includes(keywordInput.trim())) {
      setEditingTag({
        ...editingTag,
        keywords: [...currentKeywords, keywordInput.trim()]
      });
    }
    setKeywordInput("");
  };

  // Remove keyword from editing tag
  const handleRemoveKeyword = (keyword: string) => {
    if (!editingTag) return;
    
    const currentKeywords = normalizeKeywords(editingTag.keywords);
    setEditingTag({
      ...editingTag,
      keywords: currentKeywords.filter(k => k !== keyword)
    });
  };

  // Start creating new tag
  const handleStartCreateTag = () => {
    setNewTag({ name: "", description: "", keywords: [] });
  };

  // Cancel creating new tag
  const handleCancelCreate = () => {
    setNewTag(null);
    setNewTagKeywordInput("");
  };

  // Add keyword to new tag
  const handleAddNewTagKeyword = () => {
    if (!newTag || !newTagKeywordInput.trim()) return;
    
    const currentKeywords = normalizeKeywords(newTag.keywords);
    if (!currentKeywords.includes(newTagKeywordInput.trim())) {
      setNewTag({
        ...newTag,
        keywords: [...currentKeywords, newTagKeywordInput.trim()]
      });
    }
    setNewTagKeywordInput("");
  };

  // Remove keyword from new tag
  const handleRemoveNewTagKeyword = (keyword: string) => {
    if (!newTag) return;
    
    const currentKeywords = normalizeKeywords(newTag.keywords);
    setNewTag({
      ...newTag,
      keywords: currentKeywords.filter(k => k !== keyword)
    });
  };

  // Create new tag
  const handleCreateTag = async () => {
    if (!newTag || !newTag.name) {
      toast({
        title: "Error creating tag",
        description: "Tag name is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Format keywords for storage
      const keywordsForStorage = normalizeKeywords(newTag.keywords);

      const { data, error } = await supabase
        .from('tags')
        .insert({
          name: newTag.name,
          description: newTag.description || null,
          keywords: keywordsForStorage
        })
        .select();

      if (error) {
        throw error;
      }

      toast({
        title: "Tag created",
        description: "The new tag was successfully created.",
      });

      // Refresh tag list
      fetchTags();
      setNewTag(null);
      setNewTagKeywordInput("");
    } catch (error) {
      console.error('Error creating tag:', error);
      toast({
        title: "Error creating tag",
        description: "There was a problem creating the tag.",
        variant: "destructive",
      });
    }
  };

  // Open delete confirmation
  const handleConfirmDelete = (id: string) => {
    setDeleteTagId(id);
  };

  // Close delete confirmation
  const handleCancelDelete = () => {
    setDeleteTagId(null);
  };

  // Delete tag
  const handleDelete = async () => {
    if (!deleteTagId) return;

    try {
      // First check if this tag is in use and count mappings
      const { data: mappings, error: mappingError } = await supabase
        .from('tags_map')
        .select('id')
        .eq('tag_id', deleteTagId);

      if (mappingError) {
        throw mappingError;
      }

      // If mappings exist, delete them first
      if (mappings && mappings.length > 0) {
        // Delete all mappings for this tag from tags_map
        const { error: deleteMapError } = await supabase
          .from('tags_map')
          .delete()
          .eq('tag_id', deleteTagId);
        
        if (deleteMapError) {
          throw deleteMapError;
        }
        
        console.log(`Removed ${mappings.length} tag mappings before deleting tag`);
      }

      // Now proceed with tag deletion
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', deleteTagId);

      if (error) {
        throw error;
      }

      toast({
        title: "Tag deleted",
        description: mappings && mappings.length > 0 
          ? `Tag and ${mappings.length} associated mapping${mappings.length === 1 ? '' : 's'} were successfully deleted.` 
          : "The tag was successfully deleted.",
      });

      // Refresh tag list
      fetchTags();
      setDeleteTagId(null);
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error deleting tag",
        description: "There was a problem deleting the tag.",
        variant: "destructive",
      });
      setDeleteTagId(null);
    }
  };
  
  // Open extract tags dialog
  const handleOpenExtractTagsDialog = () => {
    setShowExtractTagsDialog(true);
  };
  
  // Extract tags from a company's pricing page
  const handleExtractTags = async () => {
    if (!extractTagsSlug) {
      toast({
        title: "Error",
        description: "Company slug is required",
        variant: "destructive",
      });
      return;
    }
    
    setIsCreatingTask(true);
    try {
      const { data, error } = await supabase.functions.invoke('extract-tags', {
        body: {
          slug: extractTagsSlug,
          version: extractTagsVersion || undefined,
          force: extractTagsForce
        }
      });
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Tags extraction task has been started",
      });
      
      // Close the dialog and reset form
      setShowExtractTagsDialog(false);
      setExtractTagsSlug("");
      setExtractTagsVersion("");
      
      // Refresh tags after a short delay to allow for processing
      setTimeout(() => {
        fetchTags();
      }, 3000);
      
    } catch (error: any) {
      console.error('Error extracting tags:', error);
      toast({
        title: "Error extracting tags",
        description: error.message || "There was a problem starting the tags extraction task",
        variant: "destructive",
      });
    } finally {
      setIsCreatingTask(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Tag Management</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleOpenExtractTagsDialog} 
            disabled={newTag !== null || editingTag !== null}
            className="flex items-center gap-2"
          >
            Extract Tags
          </Button>
          <Button 
            onClick={handleStartCreateTag} 
            disabled={newTag !== null || editingTag !== null}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Add New Tag
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div>
          {/* New Tag Form */}
          {newTag && (
            <Card className="p-4 mb-6">
              <h3 className="text-lg font-medium mb-4">Create New Tag</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input 
                    value={newTag.name || ''}
                    onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                    placeholder="Tag name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea 
                    value={newTag.description || ''}
                    onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
                    placeholder="Tag description"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Keywords</label>
                  <div className="flex gap-2 mb-2">
                    <Input 
                      value={newTagKeywordInput}
                      onChange={(e) => setNewTagKeywordInput(e.target.value)}
                      placeholder="Enter keyword"
                      className="flex-grow"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newTagKeywordInput.trim()) {
                          e.preventDefault();
                          handleAddNewTagKeyword();
                        }
                      }}
                    />
                    <Button 
                      onClick={handleAddNewTagKeyword}
                      disabled={!newTagKeywordInput.trim()}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {normalizeKeywords(newTag.keywords).map((keyword, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {keyword}
                        <X 
                          className="h-3 w-3 cursor-pointer" 
                          onClick={() => handleRemoveNewTagKeyword(keyword)} 
                        />
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={handleCancelCreate}>Cancel</Button>
                  <Button onClick={handleCreateTag}>Create Tag</Button>
                </div>
              </div>
            </Card>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Name</TableHead>
                <TableHead className="w-[300px]">Description</TableHead>
                <TableHead>Keywords</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.map(tag => (
                <TableRow key={tag.id}>
                  <TableCell className="font-medium">
                    {editingTag?.id === tag.id ? (
                      <Input 
                        value={editingTag.name}
                        onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                      />
                    ) : tag.name}
                  </TableCell>
                  <TableCell>
                    {editingTag?.id === tag.id ? (
                      <Textarea 
                        value={editingTag.description || ''}
                        onChange={(e) => setEditingTag({ ...editingTag, description: e.target.value })}
                        className="w-full"
                      />
                    ) : tag.description}
                  </TableCell>
                  <TableCell>
                    {editingTag?.id === tag.id ? (
                      <div>
                        <div className="flex gap-2 mb-2">
                          <Input 
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            placeholder="Enter keyword"
                            className="flex-grow"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && keywordInput.trim()) {
                                e.preventDefault();
                                handleAddKeyword();
                              }
                            }}
                          />
                          <Button 
                            onClick={handleAddKeyword}
                            disabled={!keywordInput.trim()}
                          >
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {normalizeKeywords(editingTag.keywords).map((keyword, index) => (
                            <Badge key={index} variant="secondary" className="flex items-center gap-1">
                              {keyword}
                              <X 
                                className="h-3 w-3 cursor-pointer" 
                                onClick={() => handleRemoveKeyword(keyword)} 
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {normalizeKeywords(tag.keywords).map((keyword, index) => (
                          <Badge key={index} variant="secondary">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {editingTag?.id === tag.id ? (
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleCancelEdit}
                          className="flex items-center gap-1"
                        >
                          <X className="h-3 w-3" /> Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={handleSave}
                          className="flex items-center gap-1"
                        >
                          <Save className="h-3 w-3" /> Save
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(tag)}
                          className="flex items-center gap-1"
                          disabled={editingTag !== null || newTag !== null}
                        >
                          <Edit className="h-3 w-3" /> Edit
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => handleConfirmDelete(tag.id)}
                          className="flex items-center gap-1"
                          disabled={editingTag !== null || newTag !== null}
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tags.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No tags found. Create a new tag to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={deleteTagId !== null} onOpenChange={handleCancelDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the tag.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          {/* Extract Tags Dialog */}
          <Dialog open={showExtractTagsDialog} onOpenChange={(open) => {
            if (!open) {
              setShowExtractTagsDialog(false);
              setExtractTagsSlug("");
              setExtractTagsVersion("");
              setExtractTagsForce(true);
            }
          }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Extract Tags</DialogTitle>
                <DialogDescription>
                  Extract tags from a company's pricing page
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="extractSlug">Company Slug (required)</Label>
                  <Input
                    id="extractSlug"
                    placeholder="e.g., company-name"
                    value={extractTagsSlug}
                    onChange={(e) => setExtractTagsSlug(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="extractVersion">Version (optional)</Label>
                  <Input
                    id="extractVersion"
                    placeholder="e.g., 2023-01-01"
                    value={extractTagsVersion}
                    onChange={(e) => setExtractTagsVersion(e.target.value)}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="extractForce"
                    checked={extractTagsForce}
                    onCheckedChange={(checked) => setExtractTagsForce(checked as boolean)}
                  />
                  <Label htmlFor="extractForce">Force (re-process even if tags already exist)</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  onClick={handleExtractTags} 
                  disabled={isCreatingTask || !extractTagsSlug}
                >
                  {isCreatingTask ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Extract Tags'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
